/** The staff console shell.
 *
 * This is the one screen in Nudge that is not phone-shaped. It opts out of the
 * 520px frame with `.app--wide` and renders no tab bar — you get here from the
 * Profile screen or a direct link, and you leave through "Back to app".
 */

import { useEffect } from 'react'
import { useStore } from '../../lib/store'
import {
  ADMIN_SECTIONS,
  adminHref,
  type AdminRoute,
  type AdminSection,
} from '../../lib/adminRoute'
import { navigate } from '../../lib/pathname'
import { Audit } from './Audit'
import { Households, Reminders, Stores, Triggers } from './Content'
import { Overview } from './Overview'
import { System } from './System'
import { Users } from './Users'
import './admin.css'

const LABELS: Record<AdminSection, string> = {
  overview: 'Overview',
  users: 'Users',
  reminders: 'Reminders',
  households: 'Households',
  stores: 'Store cache',
  triggers: 'Nudge feed',
  system: 'System',
  audit: 'Audit',
}

export function AdminConsole({ route }: { route: AdminRoute }) {
  const user = useStore((s) => s.user)

  // The <title> is what a bookmarked console tab shows, and restoring it on
  // unmount stops the app keeping the console's title after "Back to app".
  useEffect(() => {
    const previous = document.title
    document.title = `Nudge admin — ${LABELS[route.section]}`
    return () => {
      document.title = previous
    }
  }, [route.section])

  if (!user) return null

  // The server 404s every /admin route for a non-admin regardless; this only
  // avoids rendering a console shell that would fail every request inside it.
  if (!user.is_admin) {
    return (
      <div className="staff">
        <div className="empty">
          <h2>Not found</h2>
          <p>This account does not have console access.</p>
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/')}>
            Back to Nudge
          </button>
        </div>
      </div>
    )
  }

  const openUser = (id: string) => navigate(adminHref('users', id))

  return (
    <div className="staff">
      <header className="staff-top">
        <div className="staff-top__brand">
          <span className="staff-top__pin" aria-hidden="true" />
          <div>
            <strong>Nudge admin</strong>
            <span>
              {user.display_name} · {user.is_super_admin ? 'super admin' : 'admin'}
            </span>
          </div>
        </div>
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/')}>
          Back to app
        </button>
      </header>

      <nav className="staff-nav" aria-label="Console sections">
        {ADMIN_SECTIONS.map((s) => (
          <a
            key={s}
            href={adminHref(s)}
            className={`staff-nav__item${s === route.section ? ' is-active' : ''}`}
            aria-current={s === route.section ? 'page' : undefined}
            onClick={(e) => {
              // Plain left-click routes in place; anything with a modifier is a
              // deliberate "open in a new tab" and must keep working.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
              e.preventDefault()
              navigate(adminHref(s))
            }}
          >
            {LABELS[s]}
          </a>
        ))}
      </nav>

      <main className="staff-body">
        {route.section === 'overview' && <Overview />}
        {route.section === 'users' && (
          <Users
            key={route.id ?? 'list'}
            isSuperAdmin={user.is_super_admin}
            selfId={user.id}
            {...(route.id ? { initialUserId: route.id } : {})}
          />
        )}
        {route.section === 'reminders' && <Reminders onOpenUser={openUser} />}
        {route.section === 'households' && <Households isSuperAdmin={user.is_super_admin} />}
        {route.section === 'stores' && <Stores isSuperAdmin={user.is_super_admin} />}
        {route.section === 'triggers' && <Triggers onOpenUser={openUser} />}
        {route.section === 'system' && <System isSuperAdmin={user.is_super_admin} />}
        {route.section === 'audit' && <Audit />}
      </main>
    </div>
  )
}
